import React, { useEffect, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import ReactDatePicker from "react-datepicker";

import "react-datepicker/dist/react-datepicker.css";
import "./AppDatePicker_New.scss";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendarDays } from "@fortawesome/pro-light-svg-icons";

export interface AppDatePickerProps {
  name: string;

  label?: React.ReactNode;
  required?: boolean;
  controlId?: string;
  className?: string;
  labelClassName?: string;

  placeholder?: string;
  dateFormat?: string;
  onCustomChange?: (date: Date | null) => void;
  disabled?: boolean;
  clear?: boolean;
  setDate?: Date | null;
  setMinDate?: Date | null;
  setMaxDate?: Date | null;
}

interface RoundedSelectOption {
  value: number;
  label: string;
}

interface RoundedSelectProps {
  ariaLabel: string;
  value: number;
  options: RoundedSelectOption[];
  onChange: (value: number) => void;
  compact?: boolean;
}

const normalizeDate = (value: unknown): Date | null => {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  if (typeof value === "string" || typeof value === "number") {
    const parsedDate = new Date(value);
    return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
  }

  return null;
};

const RoundedSelect: React.FC<RoundedSelectProps> = ({
  ariaLabel,
  value,
  options,
  onChange,
  compact = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const selectedOptionRef = useRef<HTMLButtonElement>(null);

  const selectedOption = options.find(
    (option) => option.value === value
  );

  useEffect(() => {
    const closeOnOutsideClick = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", closeOnOutsideClick);
    document.addEventListener("keydown", closeOnEscape);

    return () => {
      document.removeEventListener("mousedown", closeOnOutsideClick);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, []);

  useEffect(() => {
    if (!isOpen || !selectedOptionRef.current) {
      return;
    }

    const option = selectedOptionRef.current;
    const menu = option.parentElement;

    if (menu) {
      menu.scrollTop =
        option.offsetTop -
        menu.clientHeight / 2 +
        option.clientHeight / 2;
    }
  }, [isOpen]);

  return (
    <div
      ref={rootRef}
      className={`app-date-picker-select${
        compact ? " app-date-picker-select--compact" : ""
      }`}
    >
      <button
        type="button"
        className="app-date-picker-select__trigger"
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        onClick={() => setIsOpen((open) => !open)}
      >
        <span>{selectedOption?.label}</span>

        <span
          className="app-date-picker-select__chevron"
          aria-hidden="true"
        />
      </button>

      {isOpen && (
        <div
          className="app-date-picker-select__menu"
          role="listbox"
          aria-label={ariaLabel}
        >
          {options.map((option) => (
            <button
              type="button"
              role="option"
              aria-selected={option.value === value}
              ref={
                option.value === value
                  ? selectedOptionRef
                  : undefined
              }
              className={`app-date-picker-select__option${
                option.value === value ? " is-selected" : ""
              }`}
              key={option.value}
              onClick={() => {
                onChange(option.value);
                setIsOpen(false);
              }}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export const AppDatePicker_New: React.FC<AppDatePickerProps> = ({
  name,

  // New label-related props
  label,
  required = false,
  controlId,
  className = "",
  labelClassName = "",

  placeholder,
  onCustomChange,
  dateFormat = "MM/dd/yyyy",
  disabled = false,
  clear = false,
  setDate,
  setMinDate,
  setMaxDate,
}) => {
  const {
    register,
    setValue,
    getValues,
    formState: { errors },
    clearErrors,
    setError,
  } = useFormContext();

  const initialDate = normalizeDate(
    getValues(name) ?? setDate
  );

  const [selectedDate, setSelectedDate] =
    useState<Date | null>(initialDate);

  const [isCalendarOpen, setIsCalendarOpen] =
    useState(false);

  const [dateBeforeOpen, setDateBeforeOpen] =
    useState<Date | null>(null);

  const monthOptions = Array.from(
    { length: 12 },
    (_, month) => ({
      value: month,
      label: new Date(
        2000,
        month
      ).toLocaleString("en-US", {
        month: "long",
      }),
    })
  );

  const yearOptions = Array.from(
    { length: 201 },
    (_, index) => {
      const year =
        new Date().getFullYear() - 100 + index;

      return {
        value: year,
        label: String(year),
      };
    }
  );

  const hasError = !!errors[name];

  const errorMessage: string =
    typeof errors[name]?.message === "string"
      ? String(errors[name]?.message)
      : "";

  /*
   * Register the field and populate the initial value.
   */
  useEffect(() => {
    register(name);

    if (setDate !== undefined) {
      setSelectedDate(setDate);
      setValue(name, setDate);
    }
  }, [
    register,
    name,
    setDate,
    setValue,
  ]);

  /*
   * Handle date selected from the calendar.
   */
  const handleDateChange = (
    date: Date | null
  ) => {
    setSelectedDate(date);

    setValue(name, date, {
      shouldDirty: true,
      shouldValidate: true,
    });

    if (hasError && date) {
      clearErrors(name);
    }

    onCustomChange?.(date);
  };

  const openCalendar = () => {
    if (!isCalendarOpen) {
      setDateBeforeOpen(selectedDate);
    }

    setIsCalendarOpen(true);
  };

  const cancelSelection = () => {
    setSelectedDate(dateBeforeOpen);

    setValue(name, dateBeforeOpen, {
      shouldDirty: true,
      shouldValidate: true,
    });

    setIsCalendarOpen(false);
  };

  const confirmSelection = () => {
    setIsCalendarOpen(false);
  };

  const formatDisplayDate = (
    date: Date | null
  ) => {
    if (!date) {
      return "Choose a date";
    }

    return date.toLocaleDateString(
      "en-US",
      {
        weekday: "short",
        month: "short",
        day: "numeric",
      }
    );
  };

  /*
   * Prevent Enter from submitting the form
   * while interacting with the date picker.
   */
  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLElement>
  ) => {
    if (e.key === "Enter") {
      e.preventDefault();
    }
  };

  /*
   * Handle manually typed dates.
   */
  const handleRawDateChange = (
    event?:
      | React.MouseEvent<HTMLElement>
      | React.KeyboardEvent<HTMLElement>
  ) => {
    if (!event) {
      return;
    }

    const input =
      event.target as HTMLInputElement | null;

    if (!input) {
      return;
    }

    const rawValue =
      input.value?.trim();

    if (
      !rawValue ||
      rawValue.length < 8
    ) {
      return;
    }

    const parts =
      rawValue.split("/");

    if (parts.length !== 3) {
      return;
    }

    const month = Number(parts[0]);
    const day = Number(parts[1]);
    const year = Number(parts[2]);

    if (
      !Number.isInteger(month) ||
      !Number.isInteger(day) ||
      !Number.isInteger(year)
    ) {
      return;
    }

    if (
      month < 1 ||
      month > 12 ||
      day < 1 ||
      day > 31 ||
      year < 1000 ||
      year > 9999
    ) {
      return;
    }

    const parsedDate = new Date(
      year,
      month - 1,
      day
    );

    /*
     * Prevent JavaScript from silently
     * converting invalid dates.
     */
    if (
      parsedDate.getFullYear() !== year ||
      parsedDate.getMonth() !== month - 1 ||
      parsedDate.getDate() !== day
    ) {
      return;
    }

    parsedDate.setHours(0, 0, 0, 0);

    /*
     * Validate minimum date.
     */
    if (setMinDate) {
      const minDate =
        new Date(setMinDate);

      minDate.setHours(0, 0, 0, 0);

      if (parsedDate < minDate) {
        setError(name, {
          type: "manual",
          message: `The date must be on or after ${setMinDate.toLocaleDateString()}`,
        });

        setSelectedDate(parsedDate);
        setValue(name, parsedDate);

        return;
      }
    }

    /*
     * Validate maximum date.
     */
    if (setMaxDate) {
      const maxDate =
        new Date(setMaxDate);

      maxDate.setHours(0, 0, 0, 0);

      if (parsedDate > maxDate) {
        setError(name, {
          type: "manual",
          message: `The date must be on or before ${setMaxDate.toLocaleDateString()}`,
        });

        setSelectedDate(parsedDate);
        setValue(name, parsedDate);

        return;
      }
    }

    clearErrors(name);

    handleDateChange(parsedDate);
  };

  /*
   * Generate a unique ID for the input.
   */
  const inputId =
    controlId || name;

  return (
    <div
      className={`app-date-picker-field ${className}`}
    >
      {label && (
  <label
    htmlFor={inputId}
    className={`app-date-picker-field__label ${labelClassName}`}
  >
    {label}

    {required && (
      <span
        className="text-danger"
        aria-hidden="true"
      >
        {" "}
        *
      </span>
    )}
  </label>
)}

      <div
        className={`custom-datepicker ${
          hasError
            ? "custom-datepicker--error"
            : ""
        }`}
      >
        <ReactDatePicker
          {...register(name)}
          id={inputId}
          wrapperClassName={name}
          selected={
            selectedDate ?? undefined
          }
          onChange={handleDateChange}
          onKeyDown={handleKeyDown}
          open={isCalendarOpen}
          onInputClick={openCalendar}
          onClickOutside={() =>
            setIsCalendarOpen(false)
          }
          shouldCloseOnSelect={false}
          dateFormat={dateFormat}
          className="form-control"
          calendarClassName="app-date-picker-calendar"
          popperClassName="app-date-picker-popper"
          showIcon
          icon={
            <FontAwesomeIcon
              icon={faCalendarDays}
            />
          }
          placeholderText={placeholder}
          renderCustomHeader={({
            date,
            changeMonth,
            changeYear,
            decreaseMonth,
            increaseMonth,
            prevMonthButtonDisabled,
            nextMonthButtonDisabled,
          }) => (
            <div className="app-date-picker-header">
              <div className="app-date-picker-prompt">
                Select date
              </div>

              <div className="app-date-picker-selected-date">
                <span>
                  {formatDisplayDate(
                    selectedDate
                  )}
                </span>

                <span
                  className="app-date-picker-edit"
                  aria-hidden="true"
                >
                  &#9998;
                </span>
              </div>

              <div className="app-date-picker-navigation">
                <div className="app-date-picker-month-year">
                  <RoundedSelect
                    ariaLabel="Month"
                    value={date.getMonth()}
                    options={monthOptions}
                    onChange={changeMonth}
                  />

                  <RoundedSelect
                    ariaLabel="Year"
                    value={date.getFullYear()}
                    options={yearOptions}
                    onChange={changeYear}
                    compact
                  />
                </div>

                <div className="app-date-picker-arrows">
                  <button
                    type="button"
                    aria-label="Previous month"
                    onClick={decreaseMonth}
                    disabled={
                      prevMonthButtonDisabled
                    }
                  >
                    &#8249;
                  </button>

                  <button
                    type="button"
                    aria-label="Next month"
                    onClick={increaseMonth}
                    disabled={
                      nextMonthButtonDisabled
                    }
                  >
                    &#8250;
                  </button>
                </div>
              </div>
            </div>
          )}
          autoComplete="off"
          disabled={disabled}
          isClearable={clear}
          minDate={
            setMinDate ?? undefined
          }
          maxDate={
            setMaxDate ?? undefined
          }
          onChangeRaw={
            handleRawDateChange
          }
        >
          <div className="app-date-picker-actions">
            <button
              type="button"
              onClick={cancelSelection}
            >
              Cancel
            </button>

            <button
              type="button"
              onClick={confirmSelection}
            >
              OK
            </button>
          </div>
        </ReactDatePicker>

        <div className="form-text text-muted date-format-mask">
          MM/DD/YYYY
        </div>
      </div>

      {hasError && (
        <div className="error">
          {errorMessage}
        </div>
      )}
    </div>
  );
};

export default AppDatePicker_New;